import { OnboardingInfoDto, OnboardingMode } from '@interfaces/models';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Routes } from '@shared/enums';

const BULK_MODE_STEPS = [Routes.IMPORT_SUMMARY, Routes.IMPORT_VEHICLES, Routes.IMPORT_EMPLOYEES];

const ROUTES_IN_ORDER = [
  {
    key: 'ADD_VEHICLES',
    routes: [Routes.ADD_VEHICLES],
    mode: 'singleOnly',
  },
  {
    key: 'ADD_EMPLOYEES',
    routes: [Routes.ADD_EMPLOYEES],
    mode: 'singleOnly',
  },
  {
    key: 'IMPORT_SUMMARY',
    routes: [Routes.IMPORT_SUMMARY],
    mode: 'bulkOnly',
  },
  {
    key: 'IMPORT_VEHICLES',
    routes: [Routes.IMPORT_VEHICLES],
    mode: 'bulkOnly',
    isChild: true,
  },
  {
    key: 'IMPORT_EMPLOYEES',
    routes: [Routes.IMPORT_EMPLOYEES],
    mode: 'bulkOnly',
    isChild: true,
  },
  {
    key: 'TOP_UP_BALANCE',
    routes: [Routes.TOP_UP_BALANCE, Routes.TOP_UP_BALANCE_SUCCESS, Routes.TOP_UP_BALANCE_FAILED],
    mode: 'all',
  },
  {
    key: 'SUBSCRIPTIONS',
    routes: [Routes.SUBSCRIPTIONS],
    mode: 'noSubscriptions',
  },
  {
    key: 'COMPANY_POLICY',
    routes: [Routes.COMPANY_POLICY],
    mode: 'all',
  },
];

export const useOnboardingStepper = ({
  hasSubscriptionsAssigned,
  mode,
}: Partial<OnboardingInfoDto>) => {
  const [isBulkMode, setIsBulkMode] = useState<boolean>(mode === OnboardingMode.BULK);
  const { pathname } = useLocation();

  useEffect(() => {
    /* Check if user enters bulk mode */
    const isBulkStep = BULK_MODE_STEPS.some((step) => pathname === `${Routes.ONBOARDING}/${step}`);
    if (isBulkMode !== isBulkStep) setIsBulkMode(isBulkStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const routesInOrder = ROUTES_IN_ORDER
    /* Filter routes by mode */
    .filter((element) => {
      if (element.mode === 'noSubscriptions') return !hasSubscriptionsAssigned;
      if (element.mode === 'singleOnly') return !isBulkMode;
      if (element.mode === 'bulkOnly') return isBulkMode;
      return true;
    })
    /* Map and add active and completed properties */
    .map((element, index, filteredArray) => {
      const isActive = element.routes.some((route) => pathname === `${Routes.ONBOARDING}/${route}`);
      const activeIndex = filteredArray.findIndex((item) =>
        item.routes.some((route) => pathname === `${Routes.ONBOARDING}/${route}`),
      );

      return {
        ...element,
        active: isActive,
        completed: activeIndex > index,
        idx: filteredArray.filter((item) => !item.isChild).indexOf(element) + 1,
      };
    });

  return { isBulkMode, routesInOrder };
};
